"use client";

import { Suspense, useState, type FormEvent } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { login, ApiClientError } from "@/lib/auth";
import { STORAGE_KEY } from "@/contexts/product-context";

function LoginForm() {
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const emailError = email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("メールアドレスとパスワードを入力してください");
      return;
    }

    if (emailError) {
      setError("有効なメールアドレスを入力してください");
      return;
    }

    setIsLoading(true);

    try {
      const selectedProductId =
        typeof window !== "undefined"
          ? (window.localStorage.getItem(STORAGE_KEY) ?? undefined)
          : undefined;
      await login({ email, password, selectedProductId });
      // ログイン成功後のリダイレクト先を決定する。
      // proxy.ts がセッション切れを検知した際に付与する `from` クエリパラメータ（遷移前のパス）を取得し、
      // オープンリダイレクト防止のため "/" 始まり以外はデフォルトにフォールバックする。
      // router.push() を使わず window.location.href でフルリロードするのは、
      // セッション切れ後の /login リダイレクト経由でログインした場合に Next.js Router キャッシュの
      // 状態不整合で router.push() が動作しないケースを回避するため。
      const rawFrom = searchParams.get("from") ?? "/";
      const from = rawFrom.startsWith("/") ? rawFrom : "/";
      window.location.href = from;
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(
          err.status === 401 ? "メールアドレスまたはパスワードが正しくありません" : err.message,
        );
      } else {
        setError("ログインに失敗しました。しばらくしてからお試しください。");
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="mb-4">
        <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-ar-gray-700">
          メールアドレス
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="block w-full rounded-lg border border-ar-gray-300 px-3 py-2 text-sm text-ar-gray-900 placeholder-ar-gray-400 outline-none transition focus:border-ar-blue-500 focus:ring-2 focus:ring-ar-blue-500/20"
          placeholder="admin@example.com"
          disabled={isLoading}
        />
        {emailError && (
          <p className="mt-1 text-xs text-red-500">有効なメールアドレスを入力してください</p>
        )}
      </div>

      <div className="mb-6">
        <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-ar-gray-700">
          パスワード
        </label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="block w-full rounded-lg border border-ar-gray-300 px-3 py-2 text-sm text-ar-gray-900 placeholder-ar-gray-400 outline-none transition focus:border-ar-blue-500 focus:ring-2 focus:ring-ar-blue-500/20"
          placeholder="パスワード"
          disabled={isLoading}
        />
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="mb-4 text-right">
        <Link
          href="/auth/forgot-password"
          className="text-xs text-ar-gray-500 hover:text-ar-gray-700"
        >
          パスワードをお忘れですか？
        </Link>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="flex w-full items-center justify-center rounded-lg bg-ar-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-ar-blue-700 focus:outline-none focus:ring-2 focus:ring-ar-blue-500/20 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isLoading ? (
          <svg
            className="h-5 w-5 animate-spin text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : (
          "ログイン"
        )}
      </button>
    </form>
  );
}

export default function LoginPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ar-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="rounded-xl border border-ar-gray-200 bg-white px-8 py-10 shadow-sm">
          <h1 className="mb-1 text-center text-2xl font-bold text-ar-gray-900">faqrun</h1>
          <p className="mb-8 text-center text-sm text-ar-gray-500">管理画面にログイン</p>
          <Suspense>
            <LoginForm />
          </Suspense>
        </div>
      </div>
    </div>
  );
}
